// Keyboard input. Held keys are collected into `keys` (read by the game loop).
// While the chat box is focused, typing is left to it. Enter opens chat, B
// toggles bloom, and the arrow keys are prevented from scrolling the page.
//
// Shared action hotkeys that need to yield between systems (Talk vs hold-T
// world-clock FF) resolve here so a single press-edge owns the key.

import { keys, game } from "./state.ts";
import { openChat, chatTyping } from "./chat.ts";
import { toggleInventory } from "./inventory.ts";
import { toggleJournal } from "./journal.ts";
import { toggleMinimapMax } from "./minimap.ts";
import { toggleImDebug } from "./imdebug.ts";
import { toggleSettings } from "./settings.ts";
import { toggleWeatherDev } from "./weatherdev.ts";
import { toggleToonDev } from "./toondev.ts";
import { toggleCameraDev } from "./cameradev.ts";
import { toggleGrantDev } from "./grantdev.ts";
import { sendTodFF } from "./net.ts";
import { tryTalkHotkey } from "./talk.ts";

const ARROWS = ["arrowup", "arrowdown", "arrowleft", "arrowright"];

/** True while a text field owns the keyboard (chat, F8 grant filter, …) so
 *  WASD doesn't walk and digits don't fire combat slots. */
function typingUi(): boolean {
  if (chatTyping()) return true;
  const el = document.activeElement as HTMLElement | null;
  if (!el) return false;
  const tag = el.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || el.isContentEditable;
}

export function initInput(toggleBloom: () => void) {
  addEventListener("keydown", (e) => {
    // F-keys always toggle their panels (even while a filter is focused).
    const k = e.key.toLowerCase();
    if (k === "f3") { toggleImDebug(); e.preventDefault(); return; }
    if (k === "f4") { toggleWeatherDev(); e.preventDefault(); return; }
    // F5 left to browser reload.
    if (k === "f6") { toggleToonDev(); e.preventDefault(); return; }
    if (k === "f7") { toggleCameraDev(); e.preventDefault(); return; }
    if (k === "f8") { toggleGrantDev(); e.preventDefault(); return; }
    if (typingUi()) return; // let chat / grant filter / any text field capture typing
    if (k === "enter") { openChat(); e.preventDefault(); return; }
    // T: Talk when the Talk slot is live (proximity soft-target or focused
    // talker); otherwise hold-T still fast-forwards the shared world clock.
    // Press-edge only (keydown auto-repeats while held).
    if (k === "t" && !keys.has("t")) {
      if (!tryTalkHotkey()) sendTodFF(true);
    }
    // M maximizes/restores the minimap — fire once per press (guard before add).
    if (k === "m" && !keys.has("m")) toggleMinimapMax();
    // J opens/closes the journal — fire once per press (guard before add).
    if (k === "j" && !keys.has("j")) toggleJournal();
    // B (bloom) / I (inventory) are also press-edge toggles — without the guard,
    // keydown auto-repeat while held strobes them open/closed at the OS repeat rate.
    if (k === "b" && !keys.has("b")) toggleBloom();
    // I toggles the bag — but NOT during cook mode, where the bag is a fixed part
    // of the pan UI (enterCookMode opens it, exitCookMode closes it). Letting I
    // close it there just fights the auto-reopen after each dish (plan 25).
    if (k === "i" && !keys.has("i") && !game.cooking) toggleInventory();
    // O opens/closes the graphics-settings panel — press-edge toggle like the rest.
    if (k === "o" && !keys.has("o")) toggleSettings();
    keys.add(k);
    if (ARROWS.includes(k) || k === " ") e.preventDefault(); // space jumps, don't scroll
  });
  addEventListener("keyup", (e) => {
    const k = e.key.toLowerCase();
    if (k === "t") sendTodFF(false);
    keys.delete(k);
  });
  // If the window loses focus mid-hold (cmd-tab, another window, an OS shortcut) the
  // keyup never arrives: without this the player keeps auto-walking on return, and a
  // held T would leave the SHARED world clock fast-forwarding for everyone. Release
  // T's grip explicitly, then drop every held key.
  addEventListener("blur", () => {
    if (keys.has("t")) sendTodFF(false);
    keys.clear();
  });
}
